// In-store refund payload builder — faithful port of Android
// InStoreRefundRepository.setItemLevelRefundRequest (+ the CartData other-tax refund extensions in
// UtilsMethod.kt). Pure JS, no IO. Server endpoint: Process_refund_api/save_line_item_refund.
//
// Order-detail item fields consumed (orders/order_details cart_data — raw JSON keys):
//   price            gross per-unit price
//   discount_amt     manual item discount per unit
//   item_coupon_json.discount_amount  QV-4738 item coupon per unit (NET price subtracts it too)
//   lp_discount_amt  loyalty discount (used per-unit, exactly like Android — quirks preserved)
//   qty              REMAINING (unrefunded) quantity
//   refund_qty       quantity ALREADY refunded server-side  →  original qty = qty + refund_qty
//   default_tax_amount / other_taxes_amount   LINE tax totals over the ORIGINAL qty
//   other_taxes_desc / other_taxes_rate_desc  per-tax-name JSON strings
//   line_item_id (or id)                      server row id
//
// MODES: the caller passes the refund split { credit, debit, cash, loyalty, storeCredit, giftCard }.
// Phase 1 UI only drives cash, but the math here is the complete multi-mode port (per-item
// proportional split + per-mode tax splits + the Android caps/edge cases) so card refunds are
// UI-only later. NOTE the Android wire quirk: the CARD amount goes out in `credit_amt` while
// `debit_amt` is hardcoded "0.00" — preserved verbatim.

import { roundHalfUp, toNum } from "./money";
import { refundNcaBaseFactor } from "./orderDetail";

// Android REFUND_THRESHOLD (UtilsConstant) — sum/total and full-refund matches tolerate 2¢.
export const REFUND_THRESHOLD = 0.02;
// Android ORDER_STATUS_REFUNDED — my_order.m_status for a fully refunded order (5 = voided/cancelled).
export const ORDER_STATUS_REFUNDED = "7";
export const ORDER_STATUS_VOIDED = "5";

// Server rule (Process_refund_api): inventory is restocked UNLESS reason === 'Defective Item'.
// Android RefundFragment reason list (7). Server rule (Process_refund_api): restock UNLESS "Defective Item".
// "Other" takes free text, which is what is sent.
export const REFUND_REASONS = [
  { value: "Returned Goods", restock: true },
  { value: "Accidental Charge", restock: true },
  { value: "Cancelled Order", restock: true },
  { value: "Fraudulent Order", restock: true },
  { value: "Defective Item", restock: false },
  { value: "Out of Stock", restock: true },
  { value: "Other", restock: true, freeText: true },
];

const S = (v) => (v == null ? "" : String(v));
const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};
const money = (v) => roundHalfUp(v).toFixed(2);
// Android's isNaN/isInfinite guards after divisions.
const finite = (v) => (Number.isFinite(v) ? v : 0);

const safeJson = (s) => {
  try {
    const o = JSON.parse(s || "{}");
    return o && typeof o === "object" ? o : {};
  } catch {
    return {};
  }
};

/** Original quantity = remaining qty + already-refunded qty (the per-unit tax denominator). */
export const originalQtyOf = (item) => toInt(item.qty) + toInt(item.refund_qty);

/** NET per-unit price paid: gross − manual discount − item coupon (QV-4738), abs like Android. */
export function netUnitOf(item) {
  const coupon = toNum(item.item_coupon_json && item.item_coupon_json.discount_amount);
  // ⚠️ FLOOR at 0, never Math.abs (Android fix, 2026-07-29). On an over-discounted line — discount
  // greater than price — abs() turns the negative into a SPURIOUS POSITIVE and the customer is
  // refunded money the line never held. A no-op whenever price ≥ discount, i.e. every normal line.
  return Math.max(toNum(item.price) - (toNum(item.discount_amt) + coupon), 0);
}

/** Per-unit total tax (default + other) over the ORIGINAL quantity. */
export function perUnitTaxOf(item) {
  const oq = originalQtyOf(item);
  return finite((toNum(item.default_tax_amount) + toNum(item.other_taxes_amount)) / oq);
}

/**
 * Container deposit (CRV) charged per unit on this line; 0 when none. Its tax is
 * already inside default/other tax, so only the deposit itself is added here.
 */
export function depositUnitOf(item) {
  return toNum(item.deposit_per_item);
}

/** Refund value of `sel` units of an item (net unit + per-unit tax + deposit) — what the row shows in the UI. */
export function rowRefundAmount(item, sel) {
  return (perUnitTaxOf(item) + netUnitOf(item) + depositUnitOf(item)) * (toInt(sel) || 0);
}

/**
 * Android RefundFragment :1384-1388 (QV-7124) — the line-item SELECTION value includes the loyalty
 * points spent on the line: (tax/u + max(price − disc − coupon + lp, 0)) × sel. The LP part is then
 * refunded as points (forced, locked) and cash/card cover the rest.
 */
export function rowRefundValueWithLp(item, sel) {
  const coupon = toNum(item.item_coupon_json && item.item_coupon_json.discount_amount);
  const unit = Math.max(toNum(item.price) - toNum(item.discount_amt) - coupon + toNum(item.lp_discount_amt), 0);
  return (perUnitTaxOf(item) + unit) * (toInt(sel) || 0);
}

/** Android getOtherTaxRefundList: per tax name, (line amount / original qty) × sel, half-up. */
/** Android lineFactor (fb3e18957a): a zero-price, zero-tax line keeps 1.0; every other line the order factor. */
export function refundLineFactor(item, factor) {
  const lineTax = toNum(item && item.default_tax_amount) + toNum(item && item.other_taxes_amount);
  return toNum(item && item.price) <= 0 && lineTax <= 0 ? 1 : toNum(factor) || 1;
}

export function otherTaxRefundList(item, sel) {
  const desc = safeJson(item.other_taxes_desc);
  const oq = originalQtyOf(item);
  const out = [];
  for (const name of Object.keys(desc)) {
    const perUnit = finite(toNum(desc[name]) / oq);
    out.push([name, money(perUnit * sel)]);
  }
  return out;
}

/**
 * The remaining refundable order total (Android `remainingAmount`):
 * amt − already-refunded − tip − cash_discounting.
 */
/** Android truncation to cents (getTwoDigitWithoutRoundValue). */
const trunc2 = (v) => Math.trunc(toNum(v) * 100) / 100;

/**
 * Android "Maximum Refundable Amount" for the Amount ($) tab (InStoreOrderDetailRefundFragment :331-392):
 * subtotal + tax + other_taxes − Σ(coupon_code_amt + custom_discount_amt) − bogo − |mix_match|
 * − (dual ? cash_discounting : 0) − refund_amount + tip_refund_amount
 * + (dual ? 0 : cashdiscount_refund_amount) − issued gift cards − lottery tickets.
 * Tip / NCA are added on the NEXT screen, so they are never part of this cap.
 */
export function maxPartialRefundable(orderDetail, cartData = [], isDual = false) {
  const od = orderDetail || {};
  const loy = parseOrderLoyaltyJson(od);
  const rows = Array.isArray(cartData) ? cartData : [];
  const orderLevelDiscount = roundHalfUp(rows.reduce((s, r) => s + toNum(r && r.coupon_code_amt) + toNum(r && r.custom_discount_amt), 0));
  let issuedGift = 0;
  let lotteryTickets = 0;
  for (const r of rows) {
    if (!r) continue;
    if (String(r.name || "").includes("Gift Card")) issuedGift += toNum(r.price) + toNum(r.lp_discount_amt);
    // Android quirk kept: the running total is re-multiplied by each lottery row's qty.
    if (String(r.is_lottery) === "1") lotteryTickets = (lotteryTickets + toNum(r.price) + toNum(r.lp_discount_amt)) * toNum(r.qty);
  }
  const cap =
    toNum(od.subtotal) +
    toNum(od.tax) +
    toNum(od.other_taxes) -
    orderLevelDiscount -
    toNum(loy.bogo_discount) -
    Math.abs(toNum(loy.mix_match_discount)) -
    (isDual ? toNum(od.cash_discounting) : 0) -
    trunc2(od.refund_amount) +
    trunc2(od.tip_refund_amount) +
    ((isDual ? 0 : trunc2(od.cashdiscount_refund_amount)) - issuedGift - lotteryTickets);
  return roundHalfUp(Math.max(cap, 0));
}

/**
 * Store-credit refund destination (Android maxStoreCreditAmt): offered only for a customer AND while
 * something is left to give back — a 0 cap HIDES the option (it must never fall open to "any amount").
 */
export function storeCreditRefundDef(caps) {
  if (!caps || !caps.storeCredit || !(toNum(caps.storeCreditCap) > 0)) return null;
  return { key: "storeCredit", label: "Store credit", cap: roundHalfUp(toNum(caps.storeCreditCap)) };
}

/** "All to store credit": the whole goods total, never above the store-credit cap. */
export function allToStoreCreditAmount(goodsTotal, caps) {
  const def = storeCreditRefundDef(caps);
  return def ? roundHalfUp(Math.min(toNum(goodsTotal), def.cap)) : 0;
}

export function remainingRefundable(orderDetail) {
  const od = orderDetail || {};
  return toNum(od.amt) - toNum(od.refund_amount) - toNum(od.tip) - toNum(od.cash_discounting);
}

/**
 * Build the save_line_item_refund form fields — Android setItemLevelRefundRequest verbatim.
 * @param {object} p
 *  - orderDetail: the order_details `order_detail` object (raw)
 *  - items: [{ item: <cart_data row>, sel: <qty to refund now> }] — ONLY rows with sel > 0
 *  - totalItemCount: number of rows in the order's cart_data (drives is_total_refund)
 *  - modes: { credit=0, debit=0, cash=0, loyalty=0, storeCredit=0, giftCard=0 } — the refund split
 *  - tip: { on:boolean, amount:number }   nca: { on:boolean, amount:number }
 *  - reason, orderType="In-Store Order", paymentMode ("cash"|"credit"|"debit")
 *  - loyaltyPoints ("0"), lapsed: { points:0, amount:0 } — loyalty lapse (0 until customer-earn wired)
 *  - ctx: { merchantId, employeeId, advId="", shiftSetting="", customerId="", groupId, paymentDateTime }
 *  - refundPaxDetails="", cardType=""
 * @returns {{ fields: object, totalRefundAmount: number, isTotalRefund: string }}
 */
export function buildItemLevelRefund(p) {
  const {
    orderDetail = {},
    items = [],
    totalItemCount = 0,
    modes = {},
    tip = { on: false, amount: 0 },
    nca = { on: false, amount: 0 },
    reason = "",
    orderType = "In-Store Order",
    paymentMode = "cash",
    loyaltyPoints = "0",
    lapsed = { points: 0, amount: 0 },
    ctx = {},
    refundPaxDetails = "",
    cardType = "",
  } = p;

  const mode = {
    credit: toNum(modes.credit),
    debit: toNum(modes.debit),
    cash: toNum(modes.cash),
    loyalty: toNum(modes.loyalty),
    storeCredit: toNum(modes.storeCredit),
    giftCard: toNum(modes.giftCard),
  };

  const ncaFactor = refundNcaBaseFactor(p.isSurcharge, orderDetail);

  const remainingAmount = remainingRefundable(orderDetail);

  // Σ over selected rows of (perUnitTax + netUnit) × sel — the proportion denominator (NO loyalty add,
  // NOT factor-scaled: Android InStoreRefundRepository:264 — the factor applies per row below).
  const totalPriceWithTaxes = items.reduce((s, { item, sel }) => s + rowRefundAmount(item, sel), 0);

  const refundData = [];
  const combinedOtherTaxes = {}; // combineOtherTaxes: Σ per tax name across items
  const combinedOtherRates = {}; // otherTaxesRateRefundJsonObject: rate per tax name (last write wins, like Android)
  let totalRefundAmount = 0;
  let isTotalRefundedString = "";
  let creditTaxTotal = 0;
  let cashTaxTotal = 0;
  let storeCreditTaxTotal = 0;
  let giftCardTaxTotal = 0;
  let defaultTaxRefundTotal = 0;
  let otherTaxesRefundTotal = 0;

  for (const { item, sel } of items) {
    const row = {};
    row.id = S(item.line_item_id || item.id);

    const netUnit = netUnitOf(item);
    const lpPerUnit = toNum(item.lp_discount_amt); // Android uses this value per-unit in REFUND_AMOUNT
    const oq = originalQtyOf(item);
    const perUnitTax = perUnitTaxOf(item);

    // is_refunded: 1 = this row's REMAINING qty is now fully refunded, 2 = partial
    if (toInt(item.qty) === toInt(sel)) {
      row.is_refunded = "1";
      isTotalRefundedString += "1";
    } else {
      row.is_refunded = "2";
      isTotalRefundedString += "0";
    }

    const rowTotalWithTax = (perUnitTax + netUnit) * sel;
    const itemProportion = finite(rowTotalWithTax / totalPriceWithTaxes);
    // Android fb3e18957a/4e3efab692: a zero-price, zero-tax line keeps factor 1.0 (dual NCA scaling only
    // applies to lines that carried money); the per-mode tax split divides by the FACTORED row value.
    const lineFactor = refundLineFactor(item, ncaFactor);
    const taxProportionDenom = rowTotalWithTax * lineFactor;

    // Per-mode $ for this row (mode total × the row's share of the refund).
    const creditForItem = mode.credit * itemProportion;
    const debitForItem = mode.debit * itemProportion;
    const cashForItem = mode.cash * itemProportion;
    const giftCardForItem = mode.giftCard * itemProportion;
    // (Android also derives loyaltyAmtWithTaxForItem here but never puts it — the row's loyalty
    // refund uses the per-unit lp_discount_amt below instead. Omitted rather than left dead.)
    // Store credit: Android's loyalty-interplay special case, ported verbatim.
    let storeCreditForItem;
    if (mode.storeCredit > 0.01) {
      if (mode.loyalty < lpPerUnit) {
        storeCreditForItem = Math.abs((mode.storeCredit - Math.abs(lpPerUnit - mode.loyalty)) * itemProportion);
      } else {
        storeCreditForItem = Math.abs(mode.storeCredit * itemProportion);
      }
    } else {
      storeCreditForItem = 0;
    }

    // Per-mode share of THIS row's value → drives the per-mode tax splits.
    const creditProp = finite(creditForItem / taxProportionDenom);
    const debitProp = finite(debitForItem / taxProportionDenom);
    const cashProp = finite(cashForItem / taxProportionDenom);
    const storeCreditProp = finite(storeCreditForItem / taxProportionDenom);
    const giftCardProp = finite(giftCardForItem / taxProportionDenom);

    const perUnitDefaultTax = finite(toNum(item.default_tax_amount) / oq);
    // (Android also splits default/other tax per mode here but only ever puts the TOTAL per-mode
    // tax splits below — those computed-and-discarded values are omitted.)

    const taxCredit = perUnitTax * creditProp * sel;
    const taxDebit = perUnitTax * debitProp * sel;
    const taxCash = perUnitTax * cashProp * sel;
    const taxStoreCredit = perUnitTax * storeCreditProp * sel;
    const taxGiftCard = perUnitTax * giftCardProp * sel;

    const lpRefundPerUnit = finite(toNum(item.lp_discount_amt) / oq);
    const totalLoyaltyRefund = lpRefundPerUnit * sel;

    // Caps: a row's mode share never exceeds the mode total (Android's >= clamps).
    row.credit_refund = creditForItem >= mode.credit ? roundHalfUp(mode.credit) : roundHalfUp(creditForItem);
    row.debit_refund = roundHalfUp(debitForItem);
    row.cash_refund = cashForItem >= mode.cash ? roundHalfUp(mode.cash) : money(cashForItem);
    row.store_credit_refund = storeCreditForItem >= mode.storeCredit ? roundHalfUp(mode.storeCredit) : roundHalfUp(storeCreditForItem);
    row.gift_card_refund = giftCardForItem >= mode.giftCard ? roundHalfUp(mode.giftCard) : roundHalfUp(giftCardForItem);
    row.loyality_refund = roundHalfUp(totalLoyaltyRefund);

    row.credit_refund_tax = roundHalfUp(taxCredit);
    row.debit_refund_tax = roundHalfUp(taxDebit);
    row.cash_refund_tax = roundHalfUp(taxCash);
    row.store_credit_refund_tax = roundHalfUp(taxStoreCredit);
    row.gift_card_refund_tax = roundHalfUp(taxGiftCard);
    row.loyality_refund_tax = 0;

    // Per-tax-name refund splits (getOtherTaxRefundList / RateList).
    const otherRefund = otherTaxRefundList(item, sel);
    const rates = safeJson(item.other_taxes_rate_desc);
    for (const name of Object.keys(rates)) combinedOtherRates[name] = money(toNum(rates[name]));
    const rowOtherObj = {};
    for (const [name, amt] of otherRefund) {
      rowOtherObj[name] = amt;
      combinedOtherTaxes[name] = roundHalfUp((combinedOtherTaxes[name] || 0) + toNum(amt));
    }

    row.default_tax_refund_amount = money(perUnitDefaultTax * sel);
    if (otherRefund.length) {
      row.other_taxes_refund_desc = JSON.stringify(rowOtherObj);
      row.other_tax_rate_desc = S(item.other_taxes_rate_desc);
      row.other_taxes_refund_amount = roundHalfUp(otherRefund.reduce((s, [, a]) => s + toNum(a), 0));
    } else {
      row.other_taxes_refund_desc = "";
      row.other_taxes_refund_amount = "0.00";
      row.other_tax_rate_desc = S(item.other_taxes_rate_desc);
    }

    row.refund_qty = String(sel);
    // REFUND_AMOUNT includes the per-unit loyalty add (Android quirk: lp_discount_amt used raw here).
    // The container deposit is the same for every tender, so it stays outside the dual-price factor.
    const refundAmount = ((perUnitTax + netUnit) * lineFactor + lpPerUnit + depositUnitOf(item)) * sel;
    totalRefundAmount += refundAmount;
    row.refund_amount = money(refundAmount);

    creditTaxTotal += toNum(row.credit_refund_tax);
    cashTaxTotal += toNum(row.cash_refund_tax);
    storeCreditTaxTotal += toNum(row.store_credit_refund_tax);
    giftCardTaxTotal += toNum(row.gift_card_refund_tax);
    defaultTaxRefundTotal += toNum(row.default_tax_refund_amount);
    otherTaxesRefundTotal += toNum(row.other_taxes_refund_amount);

    refundData.push(row);
  }

  if (!(storeCreditTaxTotal >= 0) || !Number.isFinite(storeCreditTaxTotal)) storeCreditTaxTotal = 0;

  // is_total_refund: every cart row selected AND each fully refunded → the WHOLE remaining order.
  // ONLINE orders (is_online=1) use the Android online branch instead: the item sum plus tip +
  // convenience/delivery fees + the conFee's tax share.
  let isTotalRefund = "0";
  if (items.length === totalItemCount && totalItemCount > 0 && !isTotalRefundedString.includes("0")) {
    isTotalRefund = "1";
    if (orderType === "In-Store Order") {
      totalRefundAmount = remainingAmount;
    } else {
      const conFeeTax = toNum(orderDetail.con_fee) * (toNum(orderDetail.tax_rate) / 100);
      totalRefundAmount += toNum(orderDetail.tip) + toNum(orderDetail.con_fee) + toNum(orderDetail.del_fee) + conFeeTax;
    }
  }
  if (orderType === "In-Store Order") {
    totalRefundAmount += (tip.on ? toNum(tip.amount) : 0) + (nca.on ? toNum(nca.amount) : 0);
  }

  // Order-level other-tax merges (combineOtherTaxes + addMissingKeysWithZero/Rates).
  const orderOtherDesc = safeJson(orderDetail.other_taxes_desc);
  const orderOtherRates = safeJson(orderDetail.other_taxes_rate_desc);
  const finalOtherTaxes = {};
  for (const k of Object.keys(combinedOtherTaxes)) finalOtherTaxes[k] = combinedOtherTaxes[k];
  for (const k of Object.keys(orderOtherDesc)) if (!(k in finalOtherTaxes)) finalOtherTaxes[k] = "0.00";
  const finalOtherRates = {};
  for (const k of Object.keys(combinedOtherRates)) finalOtherRates[k] = combinedOtherRates[k];
  for (const k of Object.keys(orderOtherRates)) if (!(k in finalOtherRates)) finalOtherRates[k] = money(toNum(orderOtherRates[k]));

  const fields = {
    order_id: S(ctx.orderId || orderDetail.order_id),
    admin_id: S(ctx.merchantId),
    merchant_id: S(ctx.merchantId),
    refund_data: JSON.stringify(refundData),
    is_total_refund: isTotalRefund,
    total_refund_amount: money(totalRefundAmount),
    is_tip_refund: tip.on ? "1" : "0",
    tip_refund_amount: tip.on ? money(tip.amount) : "0.00",
    is_cashdiscount_refund: nca.on ? "1" : "0",
    cashdiscount_refund_amount: nca.on ? money(nca.amount) : "0.00",
    payment_mode: paymentMode,
    payment_date_time: S(ctx.paymentDateTime),
    employee_id: S(ctx.employeeId),
    adv_id: S(ctx.advId),
    shift_setting: S(ctx.shiftSetting),
    customer_id: S(ctx.customerId),
    debit_amt: "0.00", // Android hardcodes 0 — the card amount rides in credit_amt below
    cash_amt: money(mode.cash),
    loyalty_point_amt: money(mode.loyalty),
    store_credit_amt: mode.storeCredit <= 0.01 ? "0.00" : money(mode.storeCredit),
    loyalty_point: S(loyaltyPoints),
    reason: S(reason),
    lapsed_loyalty_point: money(lapsed.points),
    lapsed_loyalty_point_amt: money(lapsed.amount),
    gift_card_amt: money(mode.giftCard),
    credit_amt: money(mode.credit + mode.debit), // Android passes its "debitAmount" (the card amt) here
    credit_refund_tax: String(creditTaxTotal),
    debit_refund_tax: "0.00",
    cash_refund_tax: String(roundHalfUp(cashTaxTotal)),
    store_credit_refund_tax: String(roundHalfUp(storeCreditTaxTotal)),
    loyality_refund_tax: "0.00",
    gift_card_refund_tax: String(roundHalfUp(giftCardTaxTotal)),
    default_tax_rate: S(orderDetail.tax_rate),
    other_tax_rate_desc: JSON.stringify(finalOtherRates),
    default_tax_refund_amount: String(defaultTaxRefundTotal),
    other_taxes_refund_amount: String(otherTaxesRefundTotal),
    other_taxes_refund_desc: JSON.stringify(finalOtherTaxes),
    refund_pax_details: S(refundPaxDetails),
    del_fee_refund: "0.00",
    con_fee_refund: "0.00",
    card_type: S(cardType),
    group_id: S(ctx.groupId),
  };

  return { fields, totalRefundAmount: roundHalfUp(totalRefundAmount), isTotalRefund };
}

/** Cash-paid order? (payment_id "Cash" or an EBT card type — Android's cash branch.) */
export function isCashPaidOrder(orderDetail) {
  const pid = S(orderDetail && orderDetail.payment_id).toLowerCase();
  const ct = S(orderDetail && orderDetail.card_type).toLowerCase();
  return pid === "cash" || ct.includes("foodebt") || ct.includes("cashebt") || ct.includes("food ebt") || ct.includes("cash ebt");
}

/**
 * Card refund mode from the order's stored card_type ("Credit/Visa", "Debit/…"). Android derives
 * paymentMode from isCreditCard and cardType via the PAX APPLAB tag; the web's stable equivalent is
 * the card_type prefix the sale itself wrote.
 */
export function cardRefundModeOf(orderDetail) {
  const ct = S(orderDetail && orderDetail.card_type).toLowerCase();
  const isDebit = ct.startsWith("debit") || ct.includes("debit");
  return { paymentMode: isDebit ? "debit" : "credit", cardType: isDebit ? "Debit" : "Credit" };
}

/** The sale wrote its loyalty JSON into coupon_code (getLoyaltyJson) — LP/SC/GC amounts live there. */
export function parseOrderLoyaltyJson(orderDetail) {
  return safeJson(orderDetail && orderDetail.coupon_code);
}

const isEbtCard = (ct) => {
  const c = S(ct).toLowerCase();
  return c.includes("foodebt") || c.includes("cashebt") || c.includes("food ebt") || c.includes("cash ebt");
};

/**
 * Per-mode refund availability + caps — Android RefundFragment's max*Payout matrix:
 *  - already-refunded per mode from refund_data sums,
 *  - SPLIT orders: caps from the split_payments rows (cash/EBT rows → cash; card rows →
 *    pay_amount − NCA − tip; NCA cap = Σ their cash_discounting_amount),
 *  - single-tender: cash order → cash cap = amt − refunded − SC-spent; card order → NCA cap =
 *    cash_discounting, card cap = amt minus refunds/SC/NCA/tip,
 *  - LP spent / SC spent / GC number+amount / LP earned from the order's loyalty JSON,
 *  - loyalty cap for a LINE-ITEM refund = Σ lp_discount_amt × sel of the selected rows (Android
 *    maxLoyaltyAmtPayout), further capped by LP spent − already refunded LP.
 * Store credit is a DESTINATION (any amount, "all refund in store credit") — customer required.
 * Finally, an order paid ENTIRELY by loyalty / store credit / gift card (is_loyality /
 * is_store_credit / is_gift_card = "2") may only be refunded back to that same tender.
 * @returns {{cash,card,nca,tip,loyalty,giftCard:{cap,number},storeCredit:boolean,lpRatio,
 *            lpEarned:{flag,amount,points},cardTenders:[{paymentId,cardType,cap}],hasCustomer,
 *            exclusiveTender:(""|"loyalty"|"storeCredit"|"giftCard")}}
 */
export function deriveRefundModeCaps({ orderDetail, refundData = [], splitPayments = [], itemsSelected = [], isSurcharge } = {}) {
  const od = orderDetail || {};
  const loy = parseOrderLoyaltyJson(od);
  const already = {
    cash: refundData.reduce((s, r) => s + toNum(r.cash_amt), 0),
    credit: refundData.reduce((s, r) => s + toNum(r.credit_amt), 0),
    nca: refundData.reduce((s, r) => s + toNum(r.nca_amt), 0),
    tip: refundData.reduce((s, r) => s + toNum(r.tip_amt), 0),
    giftCard: refundData.reduce((s, r) => s + toNum(r.giftcard_amt), 0),
    loyalty: refundData.reduce((s, r) => s + toNum(r.loyalty_point_amt), 0),
    storeCredit: refundData.reduce((s, r) => s + toNum(r.store_credit_amt), 0),
  };
  const scSpent = toNum(loy.store_credit_amt_spent);
  // Android RefundFragment :2286 — payouts netted against the sale are refundable as cash (split) and
  // never through the card (non-split cap).
  const lotteryPay = toNum(loy.lottery_order_pay) + toNum(loy.scratch_order_pay);
  let storeCreditCap = 0;

  let cash = 0;
  let card = 0;
  let nca = 0;
  const cardTenders = [];
  const splits = (splitPayments || []).filter(Boolean);
  if (splits.length > 0) {
    cash = lotteryPay;
    for (const sp of splits) {
      const pt = S(sp.pay_type).toLowerCase();
      if (pt === "cash" || isEbtCard(sp.card_type)) {
        cash += toNum(sp.pay_amount);
      } else if ((pt === "credit" || pt === "debit") && !isEbtCard(sp.card_type)) {
        // Android RefundFragment :2479-2569 — card cap = pay − leg NCA − leg tip in BOTH modes; the leg NCA
        // refunds through the separate (opt-in) NCA field, dual price included (09-10/09-11 master).
        const splitNca = toNum(sp.cash_discounting_amount);
        const tenderCap = toNum(sp.pay_amount) - splitNca - toNum(sp.tip);
        card += tenderCap;
        nca += splitNca;
        cardTenders.push({ paymentId: S(sp.payment_id), cardType: S(sp.card_type), cap: roundHalfUp(tenderCap), last4: S(sp.last_four_digit) });
      }
    }
    // Android InStoreOrderDetailRefundFragment :1797 — SC may take back what every tender paid.
    storeCreditCap = Math.abs(scSpent + cash + card - already.cash - already.credit - already.storeCredit);
    cash = Math.max(cash - already.cash, 0);
    card = Math.max(card - already.credit, 0);
  } else if (isCashPaidOrder(od)) {
    cash = Math.max(toNum(od.amt) - already.cash - scSpent, 0);
    storeCreditCap = Math.abs(toNum(od.amt) - already.storeCredit - already.cash);
  } else {
    // Non-split card: maxNca = od.cash_discounting (PHP decrements it on every refund — never subtract
    // refunded NCA again); the card cap excludes the NCA in both modes (Android :2775).
    const ncaRaw = toNum(od.cash_discounting);
    nca = ncaRaw;
    card = Math.abs(toNum(od.amt) - already.credit - scSpent - ncaRaw - already.nca - toNum(od.tip) - already.tip - lotteryPay);
    storeCreditCap = Math.abs(toNum(od.amt) - already.storeCredit - already.credit - already.nca);
    cardTenders.push({ paymentId: S(od.payment_id), cardType: S(od.card_type), cap: roundHalfUp(card), last4: "" });
  }

  // Line-item LP cap: the loyalty discount carried by the SELECTED rows (Android maxLoyaltyAmtPayout).
  const lpOnSelected = itemsSelected.reduce((s, { item, sel }) => s + toNum(item.lp_discount_amt) * sel, 0);
  const lpSpent = toNum(loy.loyalty_point_amt_spent);
  const loyalty = Math.max(lpOnSelected, 0); // Android: forced to Σ lp × qty (spent−refunded cap commented out)
  const lpPointsSpent = toNum(loy.loyalty_point_spent);
  const lpRatio = lpSpent > 0 ? lpPointsSpent / lpSpent : 0; // points per refunded dollar (Android refundLoyaltyPointRedemption)

  const gcAmount = toNum(loy.gift_card_amount);
  const gcNumber = S(loy.gift_card_number);
  const giftCard = {
    cap: gcNumber && gcNumber !== "0.00" ? Math.max(gcAmount - already.giftCard, 0) : 0,
    number: gcNumber && gcNumber !== "0.00" ? gcNumber : "",
  };

  const hasCustomer = !!(S(od.customer_id).trim() && S(od.customer_id) !== "0");

  // Android RefundFragment (:2496 `isLoyalty == "2"` → :2544 `isStoreCredit == "2"` → :2572
  // `isGiftCard == "2"`): when ONE wallet tender covered the whole order, every other refund
  // destination is greyed out — a loyalty-only sale refunds only to loyalty (pre-checked and
  // locked there), a store-credit-only sale only to store credit, a gift-card-only sale only to
  // that card. The flags are written at sale time by `tenderFlag` in savePayload ("2" = this
  // tender covered everything, "1" = it contributed, "0" = unused), so an order that predates
  // them simply reports "" and keeps the old, unrestricted behaviour.
  // (QA: Varun 2026-08-28 "if i doing transaction entirely in loyalty i am still able to refund in
  // store credit".)
  const exclusiveTender =
    S(od.is_loyality) === "2" ? "loyalty" : S(od.is_store_credit) === "2" ? "storeCredit" : S(od.is_gift_card) === "2" ? "giftCard" : "";
  if (exclusiveTender) {
    cash = 0;
    card = 0;
    cardTenders.length = 0;
  }
  const allow = (key) => !exclusiveTender || exclusiveTender === key;

  return {
    cash: roundHalfUp(cash),
    card: roundHalfUp(card),
    // PHP decrements my_order.tip / cash_discounting per refund, so the stored values ARE the remaining
    // caps (Android maxTipAmt = od.tip, maxNca = od.cash_discounting). Split legs keep their original
    // cash_discounting_amount, so there the refunded NCA is subtracted.
    nca: roundHalfUp(Math.max(splits.length > 0 ? nca - already.nca : nca, 0)),
    tip: roundHalfUp(Math.max(toNum(od.tip), 0)),
    loyalty: allow("loyalty") ? roundHalfUp(loyalty) : 0,
    // whole-order LP cap (partial refunds): LP spent on the order − LP already refunded
    lpSpentRemaining: allow("loyalty") ? roundHalfUp(Math.max(lpSpent - already.loyalty, 0)) : 0,
    giftCard: allow("giftCard") ? giftCard : { cap: 0, number: "" },
    storeCredit: allow("storeCredit") && hasCustomer, // "all refund in store credit" — destination gated on a customer
    storeCreditCap: roundHalfUp(storeCreditCap),
    exclusiveTender,
    lpRatio,
    lpEarned: { flag: toNum(loy.loyalty_point_amt_earned) > 0, amount: toNum(loy.loyalty_point_amt_earned), points: toNum(loy.loyalty_point_earned) },
    cardTenders,
    hasCustomer,
  };
}

/**
 * PARTIAL ($ amount) refund — Android InStoreRefundRepository.setPartialRefundRequest verbatim
 * (endpoint Process_refund_api/update_partial_refund; note: NO refund_data rows on this wire —
 * per-item detail only feeds the combined other-tax desc).
 * @param {object} p — { orderDetail, cartData (ALL rows), refundData, amount, modes, tip, nca,
 *   reason, paymentMode, cardType, refundPaxDetails, loyaltyPoints, lapsed, maxLoyaltyAmt, ctx }
 * @returns {{ fields, partialTotal:number, fully:boolean }}
 */
export function buildPartialRefund(p) {
  const {
    orderDetail = {},
    cartData = [],
    refundData = [],
    amount = 0,
    modes = {},
    tip = { on: false, amount: 0 },
    nca = { on: false, amount: 0 },
    reason = "",
    paymentMode = "cash",
    cardType = "",
    refundPaxDetails = "",
    loyaltyPoints = "0",
    lapsed = { points: 0, amount: 0 },
    maxLoyaltyAmt = 0,
    refundableCap = null, // Android stableTaxRefundableCap (Maximum Refundable Amount)
    ctx = {},
  } = p;
  const od = orderDetail;
  const mode = {
    credit: toNum(modes.credit) + toNum(modes.debit), // card rides in credit_amt (wire quirk)
    cash: toNum(modes.cash),
    loyalty: toNum(modes.loyalty),
    storeCredit: toNum(modes.storeCredit),
    giftCard: toNum(modes.giftCard),
  };
  const totalRefundable = refundableCap != null ? toNum(refundableCap) : remainingRefundable(od);
  const partialTotal = roundHalfUp(toNum(amount) + (tip.on ? toNum(tip.amount) : 0) + (nca.on ? toNum(nca.amount) : 0));

  // Per-mode taxes: proportion vs (refundable − LP max), × |Σ refunded mode taxes − order total tax|.
  const totalTaxPartial = toNum(od.tax) + toNum(od.other_taxes);
  const totalTaxRefunded = refundData.reduce(
    (s, r) =>
      s +
      toNum(r.cash_refund_tax) +
      toNum(r.debit_refund_tax) +
      toNum(r.credit_refund_tax) +
      toNum(r.store_credit_refund_tax) +
      toNum(r.gift_card_refund_tax),
    0
  );
  const denom = totalRefundable - toNum(maxLoyaltyAmt);
  const prop = (amt) => (amt > 0 ? finite(amt / denom) : 0);
  const pCard = prop(mode.credit);
  const pCash = prop(mode.cash);
  const pSC = prop(mode.storeCredit);
  const pGC = prop(mode.giftCard);
  const taxDelta = Math.abs(totalTaxRefunded - totalTaxPartial);
  const creditTax = pCard * taxDelta;
  const cashTax = pCash * taxDelta;
  const scTax = pSC * taxDelta;
  const gcTax = pGC * taxDelta;

  const defaultBase = Math.abs(toNum(od.tax) - refundData.reduce((s, r) => s + toNum(r.default_tax_refund_amount), 0));
  const finalDefaultTax = (pCard + pCash + pSC + pGC) * defaultBase;
  const otherBase = Math.abs(toNum(od.other_taxes) - refundData.reduce((s, r) => s + toNum(r.other_taxes_refund_amount), 0));
  const finalOtherTax = (pCard + pCash + pSC + pGC) * otherBase;
  // getOtherTaxRefundListPartial ratio base: order other-tax minus fully-refunded rows' refunded desc.
  const totalOtherTax = Math.abs(
    toNum(od.other_taxes) - cartData.filter((it) => toNum(it.qty) <= 0).reduce((s, it) => s + toNum(it.other_taxes_refund_amount), 0)
  );
  const ratio = finite(totalOtherTax !== 0 ? finalOtherTax / totalOtherTax : 0);
  const activeModes = [pCard, pCash, pSC, pGC].filter((x) => x > 0).length;

  // Combined per-tax-name refund desc over the remaining rows (verbatim quirk: each ACTIVE mode
  // branch adds the same perUnit × qty × ratio term).
  const combined = {};
  for (const it of cartData.filter((x) => toNum(x.qty) > 0)) {
    const desc = safeJson(it.other_taxes_desc);
    const oq = originalQtyOf(it);
    for (const k of Object.keys(desc)) {
      const perUnit = finite(toNum(desc[k]) / oq);
      combined[k] = roundHalfUp((combined[k] || 0) + perUnit * toNum(it.qty) * ratio * activeModes);
    }
  }
  const orderOtherDesc = safeJson(od.other_taxes_desc);
  for (const k of Object.keys(orderOtherDesc)) if (!(k in combined)) combined[k] = "0.00";

  const fully = Math.abs(partialTotal - roundHalfUp(totalRefundable)) <= REFUND_THRESHOLD;

  const fields = {
    order_id: S(ctx.orderId || od.order_id),
    merchant_id: S(ctx.merchantId),
    partial_refund_amount: money(partialTotal),
    is_tip_refund: tip.on ? "1" : "0",
    tip_refund_amount: tip.on ? money(tip.amount) : "0.00",
    is_cashdiscount_refund: nca.on ? "1" : "0",
    cashdiscount_refund_amount: nca.on ? money(nca.amount) : "0.00",
    payment_mode: paymentMode,
    payment_date_time: S(ctx.paymentDateTime),
    customer_id: S(ctx.customerId),
    admin_id: S(ctx.merchantId),
    debit_amt: "0.00",
    cash_amt: money(mode.cash),
    loyalty_point_amt: money(mode.loyalty),
    store_credit_amt: money(mode.storeCredit),
    loyalty_point: S(loyaltyPoints),
    reason: S(reason),
    lapsed_loyalty_point: money(lapsed.points),
    lapsed_loyalty_point_amt: money(lapsed.amount),
    total_refundable_amount: money(totalRefundable),
    m_status: fully ? ORDER_STATUS_REFUNDED : "",
    gift_card_amt: money(mode.giftCard),
    employee_id: S(ctx.employeeId),
    adv_id: S(ctx.advId),
    credit_refund_tax: String(roundHalfUp(creditTax)),
    debit_refund_tax: "0.000",
    cash_refund_tax: String(roundHalfUp(cashTax)),
    store_credit_refund_tax: String(roundHalfUp(scTax)),
    loyality_refund_tax: "0.000",
    gift_card_refund_tax: String(roundHalfUp(gcTax)),
    credit_amt: money(mode.credit),
    default_tax_rate: S(od.tax_rate),
    other_tax_rate_desc: S(od.other_taxes_rate_desc || ""),
    default_tax_refund_amount: String(roundHalfUp(finalDefaultTax)),
    other_taxes_refund_amount: String(roundHalfUp(finalOtherTax)),
    other_taxes_refund_desc: JSON.stringify(combined),
    refund_pax_details: S(refundPaxDetails),
    card_type: S(cardType),
    group_id: S(ctx.groupId),
  };
  return { fields, partialTotal, fully };
}

/**
 * Loyalty LAPSE on refund (Android RefundFragment ~1667): when the order EARNED points for a
 * customer, refunding claws them back. Base = Σ over EARN-ELIGIBLE selected rows of
 * (netUnit + perUnitTax) × sel, capped at the credit+cash refund entered; lapsedPoints =
 * pointsPerDollar × base; lapsedAmount = redemptionValue × lapsedPoints.
 */
export function computeLoyaltyLapse({ itemsSelected = [], pointsPerDollar = 0, redemptionValue = 0, capAmount = 0, maxPoints = null, partialAmount = null }) {
  let points;
  if (partialAmount != null) {
    // Partial ($) refund: points per dollar × (credit + dual NCA + cash) — no item base.
    points = toNum(pointsPerDollar) * toNum(partialAmount);
  } else {
    // Line items: Σ earn rows (tax/u + (price − discount)) × qty — NO item coupon, NO loyalty —
    // capped at credit + (dual ? NCA : 0) + cash entered. (Android abs(); the web floors at 0.)
    let base = 0;
    let anyEarn = false;
    for (const { item, sel } of itemsSelected) {
      if (S(item.earn_point) !== "1") continue;
      anyEarn = true;
      base += (perUnitTaxOf(item) + Math.max(toNum(item.price) - toNum(item.discount_amt), 0)) * sel;
    }
    if (anyEarn && base >= toNum(capAmount)) base = toNum(capAmount);
    points = toNum(pointsPerDollar) * roundHalfUp(base);
  }
  if (maxPoints != null && points > toNum(maxPoints)) points = toNum(maxPoints); // never claw back more than earned
  const amount = toNum(redemptionValue) * points;
  return { points: roundHalfUp(points), amount: roundHalfUp(amount) };
}
